import React, { useState, useRef, useEffect } from 'react';
import { Menu, X, Bell, User, LogOut, Search, PlusCircle, Heart, Settings, ChevronDown, Eye, EyeOff, MapPin, Moon, Sun, MessageSquare } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

// RentMzansi Logo Component - uses SVG icon (crisp at any size, no baked-in text)
const Logo = ({ className = "w-10 h-10" }) => (
  <img 
    src="/logo-icon.svg" 
    alt="RentMzansi" 
    className={`${className} object-contain`}
  />
);

// Theme Toggle Component - compact button for header
const ThemeToggle = () => {
  const { toggleTheme, isDark } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className="p-2.5 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-300 group"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
    >
      <div className="relative w-5 h-5">
        <Sun className={`w-5 h-5 absolute inset-0 transition-all duration-300 ${isDark ? 'opacity-0 rotate-90 scale-50' : 'opacity-100 rotate-0 scale-100'}`} />
        <Moon className={`w-5 h-5 absolute inset-0 transition-all duration-300 ${isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-50'}`} />
      </div>
    </button>
  );
};

// Mobile Theme Toggle - full width button for mobile menu
const MobileThemeToggle = () => {
  const { toggleTheme, isDark } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className="w-full flex items-center justify-between px-4 py-3 text-white/80 hover:bg-white/10 rounded-xl font-medium transition-colors"
      role="menuitem"
    >
      <div className="flex items-center gap-3">
        {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
        <span>{isDark ? 'Dark Mode' : 'Light Mode'}</span>
      </div>
      <div className={`w-10 h-6 rounded-full relative transition-colors ${isDark ? 'bg-indigo-500' : 'bg-white/30'}`}>
        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${isDark ? 'left-5' : 'left-1'}`} />
      </div>
    </button>
  );
};

export default function Header({
  currentUser,
  previewAsRenter,
  setPreviewAsRenter,
  openAuthModal,
  handleSignOut,
  setCurrentView,
  unreadCount,
  onOpenNotifications,
  unreadMessageCount = 0
}) {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowMobileMenu(false);
        setShowUserMenu(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const isLandlord = currentUser?.type === 'landlord';

  const navLinks = [
    { id: 'browse', label: 'Explore', icon: Search, show: true },
    { id: 'messages', label: 'Messages', icon: MessageSquare, show: !!currentUser, badge: unreadMessageCount },
    { id: 'add', label: 'List', icon: PlusCircle, show: currentUser && isLandlord && !previewAsRenter },
    { id: 'my-listings', label: 'My Rooms', icon: MapPin, show: currentUser && isLandlord && !previewAsRenter },
    { id: 'favorites', label: 'Saved', icon: Heart, show: true },
  ].filter(link => link.show);

  return (
    <header className="sticky top-0 z-30 bg-gradient-to-r from-[#1D3557] via-[#1D3557] to-[#2d4a6f] text-white shadow-xl shadow-blue-900/20 safe-area-top backdrop-blur-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <button 
            onClick={() => setCurrentView('browse')}
            className="flex items-center gap-2 hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-[#1D3557] rounded-lg"
            aria-label="Go to home page"
          >
            <Logo className="w-10 h-10" />
            <span className="text-xl font-extrabold hidden sm:block tracking-tight">
              <span className="text-white">Rent</span>
              <span className="text-[#E63946]">Mzansi</span>
            </span>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1" role="navigation" aria-label="Main navigation">
            {navLinks.map(link => {
              const Icon = link.icon;
              return (
                <button
                  key={link.id}
                  onClick={() => setCurrentView(link.id)}
                  className="relative flex items-center gap-2 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/10 rounded-xl font-medium text-sm transition-all duration-300 group"
                >
                  <Icon className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  {link.label}
                  {link.badge > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-red-500 to-rose-600 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg shadow-red-500/50 animate-pulse">
                      {link.badge > 9 ? '9+' : link.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-1.5">
            {/* Dark Mode Toggle */}
            <ThemeToggle />

            {/* Landlord Preview Toggle */}
            {currentUser && isLandlord && (
              <button
                onClick={() => setPreviewAsRenter(!previewAsRenter)}
                className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                  previewAsRenter
                    ? 'bg-amber-400 text-amber-900'
                    : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white'
                }`}
                title={previewAsRenter ? 'Exit renter view' : 'Preview as renter'}
                aria-pressed={previewAsRenter}
              >
                {previewAsRenter ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span className="hidden lg:inline">{previewAsRenter ? 'Exit Preview' : 'Renter View'}</span>
              </button>
            )}

            {/* Notifications */}
            {currentUser && (
              <button
                onClick={onOpenNotifications}
                className="relative p-2.5 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200"
                aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#E63946] text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse ring-2 ring-[#1D3557]">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            )}

            {/* User Menu / Auth Buttons */}
            {currentUser ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 p-1.5 pr-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-200"
                  aria-expanded={showUserMenu}
                  aria-haspopup="true"
                >
                  {currentUser.photo ? (
                    <img src={currentUser.photo} alt="" className="w-8 h-8 rounded-lg object-cover ring-2 ring-white/30" />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#E63946] to-[#c5303c] flex items-center justify-center ring-2 ring-white/30">
                      <span className="text-white text-sm font-bold">
                        {currentUser.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  )}
                  <span className="hidden sm:block text-sm font-medium text-white max-w-[100px] truncate">
                    {currentUser.name?.split(' ')[0] || 'User'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 py-2 animate-fadeIn z-50 overflow-hidden">
                    <div className="px-5 py-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border-b border-gray-100 dark:border-gray-700">
                      <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{currentUser.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize mt-0.5">{currentUser.type || 'Member'}</p>
                    </div>
                    <div className="py-2">
                      <button
                        onClick={() => { setCurrentView('profile'); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                      >
                        <div className="p-2 bg-gray-100 dark:bg-gray-600 group-hover:bg-red-100 dark:group-hover:bg-red-900/30 rounded-lg transition-colors">
                          <User className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-red-500 dark:group-hover:text-red-400" />
                        </div>
                        My Profile
                      </button>
                      {isLandlord && !previewAsRenter && (
                        <button
                          onClick={() => { setCurrentView('my-listings'); setShowUserMenu(false); }}
                          className="w-full flex items-center gap-3 px-5 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                        >
                          <div className="p-2 bg-gray-100 dark:bg-gray-600 group-hover:bg-red-100 dark:group-hover:bg-red-900/30 rounded-lg transition-colors">
                            <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-red-500 dark:group-hover:text-red-400" />
                          </div>
                          My Listings
                        </button>
                      )}
                      <button
                        onClick={() => { setCurrentView('favorites'); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                      >
                        <div className="p-2 bg-gray-100 dark:bg-gray-600 group-hover:bg-red-100 dark:group-hover:bg-red-900/30 rounded-lg transition-colors">
                          <Heart className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-red-500 dark:group-hover:text-red-400" />
                        </div>
                        Saved Rooms
                      </button>
                    </div>
                    <div className="border-t border-gray-100 dark:border-gray-700 pt-2">
                      <button
                        onClick={() => { handleSignOut(); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                          <LogOut className="w-4 h-4" />
                        </div>
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => openAuthModal('renter', 'signin')}
                  className="hidden sm:block px-5 py-2.5 text-white/90 hover:text-white font-semibold text-sm transition-all hover:bg-white/10 rounded-xl"
                >
                  Sign In
                </button>
                <button
                  onClick={() => openAuthModal('landlord', 'signup')}
                  className="px-5 py-2.5 bg-gradient-to-r from-[#E63946] to-rose-600 hover:from-[#c5303c] hover:to-rose-700 text-white font-bold text-sm rounded-xl shadow-lg shadow-red-500/40 hover:shadow-xl hover:shadow-red-500/50 transition-all duration-300 hover:scale-105 active:scale-100"
                >
                  List Room
                </button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2.5 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
              aria-label="Toggle menu"
              aria-expanded={showMobileMenu}
            >
              {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div 
          ref={mobileMenuRef}
          className="md:hidden bg-[#1D3557] border-t border-white/10 shadow-lg animate-slideDown"
          role="menu"
        >
          <nav className="px-4 py-4 space-y-1">
            {navLinks.map(link => {
              const Icon = link.icon;
              return (
                <button
                  key={link.id}
                  onClick={() => { setCurrentView(link.id); setShowMobileMenu(false); }}
                  className="w-full flex items-center justify-between px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white rounded-xl font-medium transition-colors"
                  role="menuitem"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5" />
                    {link.label}
                  </div>
                  {link.badge > 0 && (
                    <span className="w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {link.badge > 9 ? '9+' : link.badge}
                    </span>
                  )}
                </button>
              );
            })}
            
            {currentUser && isLandlord && (
              <button
                onClick={() => { setPreviewAsRenter(!previewAsRenter); setShowMobileMenu(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${
                  previewAsRenter
                    ? 'bg-amber-400 text-amber-900'
                    : 'text-white/80 hover:bg-white/10'
                }`}
                role="menuitem"
              >
                {previewAsRenter ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                {previewAsRenter ? 'Exit Renter Preview' : 'Preview as Renter'}
              </button>
            )}

            {currentUser ? (
              <>
                <div className="border-t border-white/10 my-2" />
                <MobileThemeToggle />
                <button
                  onClick={() => { setCurrentView('profile'); setShowMobileMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-white/80 hover:bg-white/10 rounded-xl font-medium transition-colors"
                  role="menuitem"
                >
                  <Settings className="w-5 h-5" />
                  Settings
                </button>
                <button
                  onClick={() => { handleSignOut(); setShowMobileMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-[#E63946] hover:bg-red-500/10 rounded-xl font-medium transition-colors"
                  role="menuitem"
                >
                  <LogOut className="w-5 h-5" />
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <div className="border-t border-white/10 my-2" />
                <MobileThemeToggle />
                <button
                  onClick={() => { openAuthModal('renter', 'signin'); setShowMobileMenu(false); }}
                  className="w-full px-4 py-3 text-white/80 hover:bg-white/10 rounded-xl font-medium transition-colors text-center"
                  role="menuitem"
                >
                  Sign In
                </button>
                <button
                  onClick={() => { openAuthModal('landlord', 'signup'); setShowMobileMenu(false); }}
                  className="w-full px-4 py-3 bg-[#E63946] hover:bg-[#c5303c] text-white rounded-xl font-semibold transition-all shadow-lg"
                  role="menuitem"
                >
                  Get Started
                </button>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
